import { helpWhy } from '../../help/why';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Paper, TextField, Typography } from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { CashIncentiveClaimService } from '../../services/cashIncentiveClaim.service';
import { CommercialSettingsService } from '../../services/commercialSettings.service';
import { ExpFormService } from '../../services/expForm.service';
import { PERMISSIONS } from '../../constants/permissions';
import { ICreateCashIncentiveClaimRequest, IExpForm } from '../../interfaces/commercial.interface';
import SearchableSelect from '../common/SearchableSelect';
import { fetchAllPages } from './common/fetchAllPages';
import { formatMoney } from '../../utils/format';


/**
 * Cash-incentive claim create form. The rate pre-fills from the institution's default and can be
 * changed per claim. The EXP picker offers only EXPs that have realized something and do not
 * already have a claim. The amount shown is a BDT preview worked out the same way the server does
 * it (each realization times its own exchange rate, then the rate %); the server still calculates
 * and stores the official figure on save.
 */
const CashIncentiveClaimForm: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.CASH_INCENTIVE_MANAGE);
    const institutionId = AuthService.getSelectedInstitution()!;
    const [exps, setExps] = useState<IExpForm[]>([]);
    const [realizedBdt, setRealizedBdt] = useState<number | null>(null);
    const [form, setForm] = useState<ICreateCashIncentiveClaimRequest>({
        expFormId: '', claimNumber: '', bankName: '', ratePercent: null, remarks: '',
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [enabled, setEnabled] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [settings, allExps, claimed] = await Promise.all([
                    CommercialSettingsService.get(institutionId),
                    fetchAllPages((page, size) => ExpFormService.list(institutionId, { page, size })),
                    fetchAllPages((page, size) =>
                        CashIncentiveClaimService.list(institutionId, page, size).then(r => r.claims)),
                ]);
                setEnabled(settings.cashIncentiveEnabled);
                setForm(f => ({ ...f, ratePercent: settings.defaultCashIncentiveRatePercent }));
                const alreadyClaimed = new Set(claimed.map(c => c.expFormId));
                setExps(allExps.filter(e => e.realizationStatus !== 'NOT_REALIZED'
                    && e.realizedAmount > 0 && !alreadyClaimed.has(e.id)));
            } catch (e) {
                toast.showApiError(e, 'Failed to load claim defaults');
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const selectedExp = exps.find(e => e.id === form.expFormId) ?? null;

    // The BDT total is the sum of each realization's amount times its own exchange rate.
    useEffect(() => {
        setRealizedBdt(null);
        if (!form.expFormId) return;
        let cancelled = false;
        (async () => {
            try {
                const page = await ExpFormService.listRealizations(institutionId, form.expFormId, 0, 200);
                if (cancelled) return;
                setRealizedBdt(page.content.reduce(
                    (sum, r) => sum + Math.round(r.amount * r.exchangeRate * 100) / 100, 0));
            } catch (e) {
                if (!cancelled) toast.showApiError(e, 'Failed to load the EXP realizations');
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.expFormId, institutionId]);

    const estimatedAmount = realizedBdt !== null && form.ratePercent
        ? Math.round(realizedBdt * form.ratePercent) / 100
        : null;

    // R-24: ratePercent is an optional per-claim override of the settings default; when present
    // it must range-check the same 0-100 the backend's @DecimalMin/@DecimalMax enforce, matching
    // CommercialSettingsPage's client-side check for the same conceptual field.
    const rateInRange = form.ratePercent === null || form.ratePercent === undefined
        || (form.ratePercent >= 0 && form.ratePercent <= 100);

    const valid = !!form.expFormId && form.claimNumber.trim().length > 0 && form.bankName.trim().length > 0
        && rateInRange;

    const saveWhy = saving ? null
        : !form.expFormId ? 'Pick an EXP form.'
        : form.claimNumber.trim().length === 0 ? 'Enter the claim number.'
        : form.bankName.trim().length === 0 ? 'Enter the bank name.'
        : !rateInRange ? 'Keep the rate between 0 and 100.'
        : null;

    const handleSave = async () => {
        try {
            setSaving(true);
            // Submit trimmed values -- @NotBlank on the backend checks trimmed length, so an
            // untrimmed value here would silently persist leading/trailing whitespace.
            const payload: ICreateCashIncentiveClaimRequest = {
                ...form,
                claimNumber: form.claimNumber.trim(),
                bankName: form.bankName.trim(),
                remarks: form.remarks?.trim() || null,
            };
            const created = await CashIncentiveClaimService.create(institutionId, payload);
            toast.showSuccess('Cash incentive claim created');
            navigate('/commercial/cash-incentive');
            return created;
        } catch (e) {
            toast.showApiError(e, 'Failed to create claim');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return null;

    if (!enabled) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ mb: 2 }}>New Cash Incentive Claim</Typography>
                <Alert severity="info" data-testid="cash-incentive-disabled-banner">
                    Cash incentive claims are disabled for this institution.
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: 720 }} data-help-page="commercial.claimForm.page">
            <Typography variant="h5" sx={{ mb: 2 }}>New Cash Incentive Claim</Typography>
            <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }} data-help-section="commercial.claimForm.section.details">
                <SearchableSelect data-help="commercial.claimForm.field.expForm" label="EXP Form" required options={exps} value={form.expFormId}
                    onChange={id => setForm({ ...form, expFormId: id })}
                    getOptionLabel={e => `${e.expNumber} — ${e.buyerName}`}
                    helperText="Only EXPs with realized money and no existing claim are listed." />
                <TextField data-help="commercial.claimForm.field.claimNumber" label="Claim Number" required value={form.claimNumber}
                    onChange={e => setForm({ ...form, claimNumber: e.target.value })} />
                <TextField data-help="commercial.claimForm.field.bankName" label="Bank Name" required value={form.bankName}
                    onChange={e => setForm({ ...form, bankName: e.target.value })} />
                <TextField data-help="commercial.claimForm.field.ratePercent" label="Rate % (optional override)" type="number" value={form.ratePercent ?? ''}
                    error={!rateInRange}
                    helperText={!rateInRange ? 'Must be between 0 and 100' : undefined}
                    onChange={e => setForm({ ...form, ratePercent: e.target.value === '' ? null : Number(e.target.value) })} />
                <TextField data-help="commercial.claimForm.field.remarks" label="Remarks" multiline minRows={2} value={form.remarks ?? ''}
                    onChange={e => setForm({ ...form, remarks: e.target.value })} />

                {estimatedAmount !== null && (
                    <Alert severity="info" data-testid="incentive-computed-amount">
                        Estimated claim amount: {formatMoney(estimatedAmount, 'BDT')} (on {formatMoney(realizedBdt, 'BDT')} realized). This is a preview; the final amount is calculated when you save.
                    </Alert>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button data-help="commercial.claimForm.cancel" onClick={() => navigate(-1)}>Cancel</Button>
                    {canManage && (
                        <Button data-help="commercial.claimForm.save" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} variant="contained" disabled={saving || !valid} onClick={handleSave}>Save</Button>
                    )}
                </Box>
            </Paper>
        </Box>
    );
};

export default CashIncentiveClaimForm;
